/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import { TableSaveViewModal } from 'carbon-addons-iot-react';
import { useIntl, defineMessages } from 'react-intl';
import noop from 'lodash/noop';

import { getDescription } from 'tableUtils';
import { TableViewType } from 'TableTypes';

const messages = defineMessages({
  updateView: 'Update view',
  saveNewView: 'Save new view',
  ascending: 'Ascending',
  descending: 'Descending',
  viewTitle: 'View title',
  saveViewBodyText: `You can save the current view's settings including applied filters and search.`,
  defaultView: 'Save as my default view',
  publicView: 'Public view',
  close: 'Close',
  save: 'Save',
  cancel: 'Cancel',
});

type SaveViewModalType = {
  view: TableViewType;
  isOpen?: boolean;
  onSave?: () => void;
  onClose?: () => void;
  onChange?: (input: Record<string, never>) => void;
  onClearError?: () => void;
  error?: string;
  isSendingData?: boolean;
};

const defaultProps = {
  isOpen: true,
  onSave: noop,
  onClose: noop,
  onChange: noop,
  onClearError: noop,
  error: null,
  isSendingData: false,
};

const SaveViewModal = ({
  view,
  isOpen = true,
  onSave = noop,
  onClose = noop,
  onChange = noop,
  onClearError = noop,
  error = null,
  isSendingData = false,
}: SaveViewModalType): JSX.Element => {
  const { formatMessage } = useIntl();
  return (
    <TableSaveViewModal
      actions={{
        onSave,
        onClose,
        onClearError,
        onChange,
      }}
      sendingData={isSendingData}
      error={error}
      open={isOpen}
      viewDescription={getDescription(view, formatMessage)}
      initialFormValues={{
        title: view.title,
        isPublic: Boolean(view.isPublic),
        isDefault: Boolean(view.isDefault),
      }}
      i18n={{
        modalTitle: view.id
          ? formatMessage(messages.updateView)
          : formatMessage(messages.saveNewView),
        modalBodyText: formatMessage(messages.saveViewBodyText),
        titleInputLabelText: formatMessage(messages.viewTitle),
        defaultCheckboxLabelText: formatMessage(messages.defaultView),
        publicCheckboxLabelText: formatMessage(messages.publicView),
        closeIconDescription: formatMessage(messages.close),
        saveButtonLabelText: formatMessage(messages.save),
        cancelButtonLabelText: formatMessage(messages.cancel),
      }}
    />
  );
};

SaveViewModal.defaultProps = defaultProps;
export default SaveViewModal;
